import { Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import SeoHead from '@/components/seo/SeoHead';

const PLANS = [
  {
    name: 'Practice',
    price: 'AUD 999',
    period: '/month',
    seats: '5 named clinician seats',
    auditorSeats: '1 free auditor seat',
    sessions: 'Unlimited standard sessions',
    voiceRefinements: '100 min voice dictation / month',
    highlight: false,
    description: 'Ideal for smaller MMHC teams starting their AI-assisted documentation journey.',
    cta: 'Book Demo',
  },
  {
    name: 'Centre',
    price: 'AUD 1,799',
    period: '/month',
    seats: '10 named clinician seats',
    auditorSeats: '2 free auditor seats',
    sessions: 'Unlimited standard sessions',
    voiceRefinements: '250 min voice dictation / month',
    highlight: true,
    description: 'The most popular choice for Medicare Mental Health Centres.',
    cta: 'Book Demo',
  },
  {
    name: 'Network / PHN',
    price: 'Custom',
    period: '',
    seats: 'Custom clinician seats',
    auditorSeats: '5 free auditor seats per site',
    sessions: 'Custom scope',
    voiceRefinements: 'Negotiated voice dictation minutes',
    highlight: false,
    description: 'For PHNs, multi-site services, and broader commissioned programs.',
    cta: 'Talk to Us',
    extraFeatures: [
      'Multi-site automated provisioning',
      'Centralised network dashboard',
      'PHN Team Management with MFA',
    ]
  },
];

const FAQS = [
  {
    q: 'Are sessions really unlimited for MMHC plans?',
    a: 'Yes — MMHC plans include unlimited standard sessions for named licensed clinician seats.',
  },
  {
    q: 'What counts as a clinician seat?',
    a: 'A clinician seat is a named licensed user who uses the clinical workflow. Admin access is not counted as a clinician seat.',
  },
  {
    q: 'What if our team grows beyond the included seats?',
    a: 'You can add additional clinician seats for AUD 189 per seat per month.',
  },
  {
    q: 'Is there an implementation fee?',
    a: 'For organisational buyers, yes — we recommend a one-off implementation fee for setup, onboarding, workflow alignment, and rollout support. This is discussed during the demo.',
  },
  {
    q: 'Do PHNs have fixed public pricing?',
    a: 'No. PHN and network pricing is custom because rollout scope and governance needs vary significantly. Contact us for a tailored proposal.',
  },
  {
    q: 'What is an auditor seat?',
    a: 'An auditor seat provides read-only access to audit logs, compliance reports, and session data for governance and oversight purposes. Auditors cannot run clinical sessions, create drafts, or perform any clinical actions.',
  },
  {
    q: 'What is the Clinical Refiner?',
    a: 'The Clinical Refiner transforms clinician shorthand, abbreviations, and rough notes into professional, AHPRA-aligned clinical documentation. It works section-by-section with two modes — Replace and Augment. All refinements are previewed before acceptance. Text refinement is unlimited on all plans.',
  },
  {
    q: 'What is the Voice-to-Note Refiner and why is it capped?',
    a: 'The Voice-to-Note Refiner lets clinicians dictate observations directly into note sections. Dictation is transcribed and refined into structured clinical language with a three-panel comparison for review. Voice dictation uses speech-to-text processing which has a per-minute cost, so each plan includes a monthly minute allowance: Practice (100 min/month), Centre (250 min/month), Network/PHN (negotiated). Text refinement remains unlimited.',
  },
  {
    q: 'What is the Referral Service Directory?',
    a: 'A searchable, tenant-scoped directory of services, practitioners, and programmes for client referrals. You can add entries manually, import from a URL, or bulk-upload via CSV. Entries include contact details, specialisations, cost type, and eligibility criteria. A 90-day verification cycle flags stale entries automatically. Layered visibility (Organisation, Regional, Global) lets you share across your PHN network.',
  },
  {
    q: 'How do Document Templates protect client privacy?',
    a: 'Document templates contain only placeholder tokens (e.g. {{ClientName}}, {{GPName}}). When you generate a document, placeholders are resolved with real data — but the generated output exists only in your browser memory. It is never saved to the Notematic database. You copy or download the result; Notematic retains no trace of the generated text.',
  },
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

export default function PricingPage() {
  return (
    <PageLayout>
      <SeoHead
        title="Notematic Pricing — MMHC Clinical Documentation Plans | Australia"
        description="Simple, predictable pricing for Medicare Mental Health Centres. Practice plan AUD $999/mo (5 seats), Centre plan AUD $1,799/mo (10 seats). Unlimited sessions. Book a demo."
        canonical="/pricing"
        jsonLd={FAQ_JSON_LD}
      />
      {/* Hero */}
      <section className="section-padding bg-gradient-to-b from-white to-secondary-50 dark:from-secondary-900 dark:to-secondary-800">
        <div className="container-custom text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-950 text-primary-700 dark:text-primary-300 text-sm font-medium mb-8">
            Transparent, predictable pricing for mental health services
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-secondary-900 dark:text-white mb-6">
            Simple pricing for{' '}
            <span className="text-gradient">Medicare Mental Health Centres</span>
          </h1>
          <p className="text-lg text-secondary-600 dark:text-secondary-300">
            Organisational plans with predictable monthly costs, unlimited standard sessions,
            and named clinician seats. No per-session invoicing noise.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 flex flex-col ${
                  plan.highlight
                    ? 'bg-primary-600 text-white shadow-lg ring-2 ring-primary-500'
                    : 'bg-secondary-50 dark:bg-secondary-900 text-secondary-900 dark:text-white border border-secondary-200 dark:border-secondary-700'
                }`}
              >
                {plan.highlight && (
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary-100 mb-4">
                    Most Popular
                  </div>
                )}
                <h2 className={`text-xl font-bold mb-2 ${plan.highlight ? 'text-white' : ''}`}>
                  {plan.name}
                </h2>
                <p className={`text-sm mb-6 ${plan.highlight ? 'text-primary-100' : 'text-secondary-500 dark:text-secondary-400'}`}>
                  {plan.description}
                </p>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.highlight ? 'text-white' : 'text-secondary-900 dark:text-white'}`}>
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className={`text-sm ml-1 ${plan.highlight ? 'text-primary-100' : 'text-secondary-500'}`}>
                      {plan.period}
                    </span>
                  )}
                </div>
                <ul className={`space-y-3 mb-8 flex-1 text-sm ${plan.highlight ? 'text-primary-100' : 'text-secondary-600 dark:text-secondary-400'}`}>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {plan.seats}
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {plan.sessions}
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {plan.auditorSeats} (read-only)
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    AI-assisted draft generation
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Clinician review controls
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Audit and export evidence
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Unlimited text refinements
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {plan.voiceRefinements}
                  </li>
                  {plan.extraFeatures?.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`btn btn-md text-center ${
                    plan.highlight
                      ? 'bg-white text-primary-700 hover:bg-primary-50'
                      : 'btn-primary'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>

          {/* Additional seats note */}
          <p className="text-center text-secondary-500 dark:text-secondary-400 text-sm mt-8">
            Additional clinician seats available at <strong className="text-secondary-700 dark:text-secondary-300">AUD 189 / seat / month</strong>
          </p>
        </div>
      </section>

      {/* What's included */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white text-center mb-10">
            Included in every organisational plan
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              'AI-assisted draft generation — 7 clinical note formats',
              'Real-time transcription workflow',
              'Clinical Refiner — unlimited text refinements',
              'Voice-to-Note Refiner — monthly minute allowance per plan',
              'Clinician review and approval controls',
              'Referral service directory with import and verification',
              'Document template management and generation',
              'Consent-aware workflow support',
              'Audit and export evidence capabilities',
              'Secure tenant-based access',
              'Standard implementation support',
              'Australian Azure infrastructure',
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3 text-secondary-700 dark:text-secondary-300">
                <svg className="w-5 h-5 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solo coming later */}
      <section className="py-12 bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <div className="rounded-xl border border-secondary-200 dark:border-secondary-700 p-8">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 text-xs font-medium mb-4">
              Coming later
            </div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
              Individual Clinician Plan
            </h3>
            <p className="text-secondary-500 dark:text-secondary-400 text-sm mb-4">
              A future solo plan at approximately AUD 249/month (up to 40 sessions) is planned after
              self-serve onboarding and billing infrastructure is production-ready.
            </p>
            <Link to="/solutions/clinicians" className="text-primary-600 dark:text-primary-400 text-sm font-medium hover:underline">
              Register your interest →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-secondary-50 dark:bg-secondary-900">
        <div className="container-custom max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white text-center mb-10">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {FAQS.map((faq) => (
              <div key={faq.q} className="rounded-xl bg-white dark:bg-secondary-800 p-6 shadow-soft">
                <h3 className="font-semibold text-secondary-900 dark:text-white mb-2">{faq.q}</h3>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-white dark:bg-secondary-950">
        <div className="container-custom max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Ready to see Notematic in your service?
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400 mb-8">
            Book a demo to see how Notematic supports clinical documentation workflows — IAR, SOAP, DAP, BIRP, GIRP, PIRP and PIE — in Medicare Mental Health Centres.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn btn-primary btn-lg">Book Demo</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Request Pilot</Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

